import {
	VOUCHER_DEMO_ACCOUNT_NAME,
	VOUCHER_DEMO_CAMPUS_ID,
	VOUCHER_DEMO_CLIENT_ID,
	VOUCHER_DEMO_MACHINE_ID,
	VOUCHER_DEMO_MACHINE_NAME
} from './voucher-demo.constants';

export const VOUCHER_DEMO_DRINKS_LAST_30D = 412;
export const VOUCHER_DEMO_DRINKS_PCT_CHANGE = 6.4;

type DemoHeatmapWeek = {
	weekStartISO: string;
	countsByDay: Array<number | null>;
};

type DemoVoucherActivityEvent = {
	type: 'issued' | 'redeemed';
	occurred_at: string;
	count: number;
	pack_id: number | null;
};

type DemoMachineBannerRow = {
	machineId: string;
	machineName: string;
	status: 'assigned' | 'none';
	assignedAt: string | null;
	imageUrl: string | null;
};

type DemoMachineRow = {
	machine_id: string;
	machine_name: string;
	campus_id: number;
	serial_no: string;
};

type DemoSalesQuery = {
	client_id?: number | null;
	campus_id?: number | null;
	start_time: string;
	end_time: string;
};

type DemoSalesSummary = {
	scope: {
		client_id: number | null;
		campus_id: number | null;
	};
	start_time: string;
	end_time: string;
	haha: {
		device_count: number;
		txn_count: number;
		refund_txn_count: number;
		unit_count: number;
		revenue_cents: number;
		devices: Array<{
			haha_market_id: string;
			client_id: number | null;
			campus_id: number | null;
			txn_count: number;
			refund_txn_count: number;
			unit_count: number;
			revenue_cents: number;
		}>;
	};
	vending: {
		status: 'stripe_readers';
		machine_count: number;
		mapped_reader_count: number;
		txn_count: number;
		refund_txn_count: number;
		revenue_cents: number | null;
	};
	purchased: {
		txn_count: number;
		revenue_cents: number;
	};
	daily_units: Array<{
		date: string;
		vending_units: number;
		haha_units: number;
	}>;
	minimum_cents: number;
	shortfall_cents: number;
	quantity: number;
};

function pad2(value: number): string {
	return value < 10 ? '0' + value : String(value);
}

function toYmd(date: Date): string {
	return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate());
}

function addDays(date: Date, days: number): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function mondayOfWeek(date: Date): Date {
	const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const diffToMonday = (monday.getDay() + 6) % 7;
	monday.setDate(monday.getDate() - diffToMonday);
	return monday;
}

function parseYmd(value: string, fallback: Date): Date {
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
	if (!match) {
		return fallback;
	}
	return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function weekdayUnits(date: Date): number {
	const day = date.getDay();
	if (day === 0 || day === 6) {
		return 6 + (day === 0 ? 1 : 0);
	}
	if (day === 2 || day === 3) {
		return 18;
	}
	if (day === 1 || day === 4) {
		return 15;
	}
	return 12;
}

export function buildDemoDrinksPeriod(): { drinksLast30d: number; pctChangeLast30d: number } {
	return {
		drinksLast30d: VOUCHER_DEMO_DRINKS_LAST_30D,
		pctChangeLast30d: VOUCHER_DEMO_DRINKS_PCT_CHANGE
	};
}

export function buildDemoSustainability(): { podsSaved: number } {
	return { podsSaved: VOUCHER_DEMO_DRINKS_LAST_30D };
}

export function buildDemoTopDrinkTypes(): {
	drinks: Array<{ id: string; name: string; drinksServed: number }>;
} {
	return {
		drinks: [
			{ id: 'latte', name: 'Latte', drinksServed: 128 },
			{ id: 'americano', name: 'Americano', drinksServed: 96 },
			{ id: 'cappuccino', name: 'Cappuccino', drinksServed: 72 },
			{ id: 'mocha', name: 'Mocha', drinksServed: 54 },
			{ id: 'espresso', name: 'Espresso', drinksServed: 38 },
			{ id: 'cold-brew', name: 'Cold Brew', drinksServed: 24 }
		]
	};
}

export function buildDemoDemandHeatmap(now: Date = new Date()): { weeks: DemoHeatmapWeek[] } {
	const currentMonday = mondayOfWeek(now);
	const todayIndex = (now.getDay() + 6) % 7;
	const weeks: DemoHeatmapWeek[] = [];
	const weekdayBoost = [0.7, 1.0, 1.15, 1.15, 1.0, 0.55, 0.45];

	for (let weekOffset = 0; weekOffset < 8; weekOffset++) {
		const weekStart = addDays(currentMonday, -weekOffset * 7);
		const weeklyScale = 0.9 + (0.05 * (8 - weekOffset));
		const countsByDay: Array<number | null> = weekdayBoost.map((boost, dayIndex) => {
			if (weekOffset === 0 && dayIndex > todayIndex) {
				return null;
			}
			return Math.round(14 * boost * weeklyScale);
		});
		weeks.push({
			weekStartISO: new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()).toISOString(),
			countsByDay
		});
	}

	return { weeks };
}

export function buildDemoMachineBanners(): { machines: DemoMachineBannerRow[] } {
	return {
		machines: [{
			machineId: VOUCHER_DEMO_MACHINE_ID,
			machineName: VOUCHER_DEMO_MACHINE_NAME,
			status: 'assigned',
			assignedAt: '2026-08-12',
			imageUrl: 'assets/demo/pacific-crest-banner.png'
		}]
	};
}

export function buildDemoVoucherActivityLedger(now: Date = new Date()): { events: DemoVoucherActivityEvent[] } {
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	return {
		events: [
			{ type: 'issued', occurred_at: toYmd(addDays(today, -74)), count: 50, pack_id: 9101 },
			{ type: 'issued', occurred_at: toYmd(addDays(today, -41)), count: 25, pack_id: 9102 },
			{ type: 'issued', occurred_at: toYmd(addDays(today, -18)), count: 1, pack_id: 9103 },
			{ type: 'issued', occurred_at: toYmd(addDays(today, -11)), count: 1, pack_id: 9104 },
			{ type: 'issued', occurred_at: toYmd(addDays(today, -4)), count: 1, pack_id: 9105 },
			{ type: 'redeemed', occurred_at: toYmd(addDays(today, -20)), count: 4, pack_id: null },
			{ type: 'redeemed', occurred_at: toYmd(addDays(today, -13)), count: 6, pack_id: null },
			{ type: 'redeemed', occurred_at: toYmd(addDays(today, -6)), count: 8, pack_id: null },
			{ type: 'redeemed', occurred_at: toYmd(addDays(today, -1)), count: 10, pack_id: null }
		]
	};
}

export function buildDemoSalesSummary(
	query: DemoSalesQuery,
	now: Date = new Date()
): DemoSalesSummary {
	const end = parseYmd(query.end_time, now);
	const start = parseYmd(query.start_time, addDays(end, -29));
	const daily_units: Array<{ date: string; vending_units: number; haha_units: number }> = [];
	let txnCount = 0;

	for (let cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
		cursor.getTime() <= end.getTime();
		cursor = addDays(cursor, 1)) {
		const vendingUnits = weekdayUnits(cursor);
		txnCount += vendingUnits;
		daily_units.push({
			date: toYmd(cursor),
			vending_units: vendingUnits,
			haha_units: 0
		});
	}

	const revenueCents = txnCount * 450;
	return {
		scope: {
			client_id: Number(VOUCHER_DEMO_CLIENT_ID),
			campus_id: Number(VOUCHER_DEMO_CAMPUS_ID)
		},
		start_time: query.start_time,
		end_time: query.end_time,
		haha: {
			device_count: 0,
			txn_count: 0,
			refund_txn_count: 0,
			unit_count: 0,
			revenue_cents: 0,
			devices: []
		},
		vending: {
			status: 'stripe_readers',
			machine_count: 1,
			mapped_reader_count: 1,
			txn_count: txnCount,
			refund_txn_count: 0,
			revenue_cents: revenueCents
		},
		purchased: {
			txn_count: 0,
			revenue_cents: 0
		},
		daily_units,
		minimum_cents: 170000,
		shortfall_cents: 0,
		quantity: 0
	};
}

export function buildDemoMachines(): DemoMachineRow[] {
	return [{
		machine_id: VOUCHER_DEMO_MACHINE_ID,
		machine_name: VOUCHER_DEMO_MACHINE_NAME + ' · ' + VOUCHER_DEMO_ACCOUNT_NAME,
		campus_id: Number(VOUCHER_DEMO_CAMPUS_ID),
		serial_no: VOUCHER_DEMO_MACHINE_ID
	}];
}
